import { useQuery } from '@tanstack/react-query'
import { ArrowRight, CalendarClock } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Alert, PageLoader } from '@/components/ui/primitives'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { ApiError, apiGet } from '@/lib/api'
import type { HomeResponse } from '@/lib/portal-types'
import {
  BlocoDeHorarios,
  CartaoDeEvento,
  CartaoDeNoticia,
  FaixaDeAvisos,
  Vazio,
} from './components'

/**
 * Home do portal.
 *
 * Uma unica chamada (`/api/portal/inicio`) traz avisos, destaques, noticias,
 * horarios e eventos. No 4G da regiao, cinco requisicoes custariam mais que a
 * consulta inteira no banco.
 *
 * Ordem das secoes: noticias, eventos, celebracoes e o cartao institucional
 * por ultimo. O aviso urgente fica acima de tudo - e o que o RF01.3 pede.
 */
export function HomePage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['portal', 'inicio'],
    queryFn: () => apiGet<HomeResponse>('/api/portal/inicio'),
    staleTime: 60 * 1000,
  })

  useDocumentTitle(data?.parish.name ?? 'Paroquia de Pacaembu')

  if (isLoading) return <PageLoader />
  if (error) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-10">
        <Alert tone="danger">
          {error instanceof ApiError
            ? error.message
            : 'Nao foi possivel carregar a pagina. Verifique sua conexao.'}
        </Alert>
      </div>
    )
  }
  if (!data) return null

  const temConteudo = data.destaques.length > 0 || data.recentes.length > 0

  return (
    <div className="mx-auto max-w-5xl space-y-10 px-4 py-6">
      {/*
       * O cartao institucional, que carregava o <h1> visivel, foi para o fim da
       * pagina. O titulo principal continua aqui, no inicio, porque quem navega
       * com leitor de tela salta de cabecalho em cabecalho: uma pagina que
       * comeca por <h2> esconde de que site se trata.
       */}
      <h1 className="sr-only">{data.parish.name}</h1>

      {/* Avisos acima de tudo: e o ponto do RF01.3. */}
      <FaixaDeAvisos avisos={data.avisos} />

      {/* 1. Noticias e avisos */}
      <section aria-labelledby="titulo-noticias">
        <div className="flex items-baseline justify-between gap-4">
          <h2 id="titulo-noticias" className="font-serif text-2xl font-semibold">
            Noticias e avisos
          </h2>
          <Link
            to="/noticias"
            className="inline-flex items-center gap-1 text-sm font-medium underline"
          >
            Ver todas
            <ArrowRight className="size-3.5" aria-hidden="true" />
          </Link>
        </div>

        {temConteudo ? (
          <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {data.destaques.map((post, indice) => (
              <CartaoDeNoticia key={post.id} post={post} destaque={indice === 0} />
            ))}
            {data.recentes.map((post) => (
              <CartaoDeNoticia key={post.id} post={post} />
            ))}
          </div>
        ) : (
          <div className="mt-4">
            <Vazio
              titulo="Nenhuma publicacao ainda"
              texto="Assim que a secretaria publicar a primeira noticia, ela aparece aqui."
            />
          </div>
        )}
      </section>

      {/* 2. Eventos */}
      {data.eventos.length > 0 && (
        <section aria-labelledby="titulo-eventos">
          <div className="flex items-baseline justify-between gap-4">
            <h2 id="titulo-eventos" className="font-serif text-2xl font-semibold">
              Proximos eventos
            </h2>
            <Link to="/eventos" className="text-sm font-medium underline">
              Ver todos
            </Link>
          </div>
          <div className="mt-4 grid gap-5 sm:grid-cols-2">
            {data.eventos.map((evento) => (
              <CartaoDeEvento key={evento.id} evento={evento} />
            ))}
          </div>
        </section>
      )}

      {/* 3. Proximas celebracoes */}
      {data.proximosHorarios.length > 0 && (
        <section aria-labelledby="titulo-horarios">
          <div className="flex items-baseline justify-between gap-4">
            <h2 id="titulo-horarios" className="font-serif text-2xl font-semibold">
              Proximas celebracoes
            </h2>
            <Link to="/horarios" className="text-sm font-medium underline">
              Ver grade completa
            </Link>
          </div>
          <div className="mt-4 rounded-lg border border-borda bg-cartao p-4">
            <BlocoDeHorarios dias={data.proximosHorarios} />
          </div>
        </section>
      )}

      {/* 4. Cartao institucional, fechando a pagina com os atalhos. */}
      <section
        aria-labelledby="titulo-paroquia"
        className="rounded-lg bg-faixa px-6 py-10 text-center text-sobre-faixa"
      >
        <h2 id="titulo-paroquia" className="font-serif text-3xl font-semibold sm:text-4xl">
          {data.parish.name}
        </h2>
        <p className="mx-auto mt-3 max-w-2xl">{data.parish.description}</p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link
            to="/horarios"
            className="inline-flex min-h-11 items-center gap-2 rounded-md bg-cartao px-4 py-2 font-semibold text-primario hover:bg-afundado"
          >
            <CalendarClock className="size-4" aria-hidden="true" />
            Horarios de missa
          </Link>
          <Link
            to="/eventos"
            className="inline-flex min-h-11 items-center gap-2 rounded-md border-2 border-sobre-faixa px-4 py-2 font-semibold hover:bg-primario-forte hover:text-sobre-primario"
          >
            Eventos e inscricoes
          </Link>
        </div>
      </section>
    </div>
  )
}
